import { create } from 'zustand';
import { getToken, setToken } from '../api/client';
import { authApi } from '../api/endpoints/auth';
import { usersApi } from '../api/endpoints/users';
import type { User } from '../api/types';

interface AuthState {
  user: User | null;
  token: string | null;
  status: 'idle' | 'loading' | 'authed' | 'guest';
  hydrate: () => Promise<void>;
  loginWithEmail: (email: string, code: string) => Promise<void>;
  loginWithPhone: (phone: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: getToken(),
  status: 'idle',

  hydrate: async () => {
    const t = getToken();
    if (!t) {
      set({ status: 'guest', user: null, token: null });
      return;
    }
    set({ status: 'loading', token: t });
    try {
      const user = await usersApi.me();
      set({ user, status: 'authed' });
    } catch {
      setToken(null);
      set({ user: null, token: null, status: 'guest' });
    }
  },

  loginWithEmail: async (email, code) => {
    const res = await authApi.verifyEmailCode(email, code);
    setToken(res.session_token);
    set({ user: res.user, token: res.session_token, status: 'authed' });
  },

  loginWithPhone: async (phone, code) => {
    const res = await authApi.verifySmsCode(phone, code);
    setToken(res.session_token);
    set({ user: res.user, token: res.session_token, status: 'authed' });
  },

  logout: async () => {
    try {
      if (get().token) await authApi.logout();
    } catch {
      /* ignore server error; clear local anyway */
    }
    setToken(null);
    set({ user: null, token: null, status: 'guest' });
  },
}));
